import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";

const DEFAULT_TARGET_LUFS = -16;
const TARGET_TP = -1.5;
const TARGET_LRA = 11;

type LoudnormMeasurement = {
    input_i: number;
    input_tp: number;
    input_lra: number;
    input_thresh: number;
    target_offset: number;
};

export type ConvertWavToMp3Options = {
    /** When true, applies EBU R128 loudnorm. Default: false (preserves exact mix gains). */
    normalize?: boolean;
    /** Target integrated loudness in LUFS. Used only when normalize is true. Default: -16. */
    targetLufs?: number;
};

/**
 * Runs ffmpeg's loudnorm filter in analyze-only mode and parses the JSON block printed to
 * stderr. Returns null if parsing fails.
 */
const measureLoudnorm = async (
    ffmpeg: FFmpeg,
    inputFile: string,
    targetLufs: number,
): Promise<LoudnormMeasurement | null> => {
    let stderrBuf = "";
    const logHandler = ({ message }: { message: string }) => {
        stderrBuf += message + "\n";
    };
    ffmpeg.on("log", logHandler);
    try {
        await ffmpeg.exec([
            "-i",
            inputFile,
            "-af",
            `loudnorm=I=${targetLufs}:TP=${TARGET_TP}:LRA=${TARGET_LRA}:print_format=json`,
            "-f",
            "null",
            "-",
        ]);
        const start = stderrBuf.lastIndexOf("{");
        const end = stderrBuf.lastIndexOf("}");
        if (start === -1 || end === -1) return null;
        const parsed = JSON.parse(stderrBuf.slice(start, end + 1));
        return {
            input_i: parseFloat(parsed.input_i),
            input_tp: parseFloat(parsed.input_tp),
            input_lra: parseFloat(parsed.input_lra),
            input_thresh: parseFloat(parsed.input_thresh),
            target_offset: parseFloat(parsed.target_offset),
        };
    } catch {
        return null;
    } finally {
        ffmpeg.off("log", logHandler);
    }
};

/**
 * Converts a WAV blob to MP3 format using FFmpeg.
 *
 * When `normalize` is true, applies EBU R128 loudnorm in two-pass LINEAR mode:
 *  - Pass 1 measures the actual loudness/peak/range of the input
 *  - Pass 2 applies a constant linear gain (no compression) using the measured values + offset
 *
 * Linear is the right choice here because the editor produces a multi-track mix where the user
 * has manually balanced voice vs music levels. Dynamic compression would flatten that balance
 * (boosting quiet music sections relative to voice). Linear preserves the artistic mix exactly,
 * accepting that loudnorm may land slightly below the target if the True Peak limit is reached.
 *
 * If the analyze pass fails, falls back to single-pass dynamic mode so we still produce a valid
 * MP3 instead of blocking the export.
 *
 * When `normalize` is false (default), the mix gains are preserved exactly — no loudness filter.
 */
export const convertWavToMp3 = async (
    wavBlob: Blob,
    onProgress?: (status: { message: string; progress?: number }) => void,
    options: ConvertWavToMp3Options = {},
): Promise<Blob> => {
    const { normalize = false, targetLufs = DEFAULT_TARGET_LUFS } = options;
    onProgress?.({ message: "Starting MP3 conversion" });

    const ffmpeg = new FFmpeg();

    try {
        onProgress?.({ message: "Loading FFmpeg", progress: 0.1 });
        await ffmpeg.load();

        onProgress?.({ message: "Processing audio", progress: 0.3 });
        await ffmpeg.writeFile("input.wav", await fetchFile(wavBlob));

        let filter: string | null = null;
        if (normalize) {
            onProgress?.({ message: "Analyzing loudness", progress: 0.4 });
            const measured = await measureLoudnorm(ffmpeg, "input.wav", targetLufs);
            if (measured) {
                filter =
                    `loudnorm=I=${targetLufs}:TP=${TARGET_TP}:LRA=${TARGET_LRA}:` +
                    `measured_I=${measured.input_i}:measured_TP=${measured.input_tp}:` +
                    `measured_LRA=${measured.input_lra}:measured_thresh=${measured.input_thresh}:` +
                    `offset=${measured.target_offset}:linear=true`;
            } else {
                console.warn("Loudnorm analyze pass failed, falling back to single-pass dynamic");
                filter = `loudnorm=I=${targetLufs}:TP=${TARGET_TP}:LRA=${TARGET_LRA}`;
            }
        }

        onProgress?.({ message: normalize ? "Normalizing & converting" : "Converting to MP3", progress: 0.6 });
        const args = ["-i", "input.wav"];
        if (filter) {
            args.push("-af", filter);
        }
        args.push("-codec:a", "libmp3lame", "-q:a", "2", "output.mp3");
        await ffmpeg.exec(args);

        onProgress?.({ message: "Finalizing", progress: 0.9 });
        const data = await ffmpeg.readFile("output.mp3");

        if (data instanceof Uint8Array) {
            return new Blob([data], { type: "audio/mp3" });
        } else {
            const encoder = new TextEncoder();
            const uint8Array = encoder.encode(data.toString());
            return new Blob([uint8Array], { type: "audio/mp3" });
        }
    } catch (error) {
        console.error("MP3 conversion error:", error);
        throw error;
    } finally {
        try {
            await ffmpeg.terminate();
        } catch (e) {
            console.warn("FFmpeg termination error:", e);
        }
    }
};
